import { useState } from "react";
import type {
  DOMAttributes,
  ReactNode,
  Ref,
  TextareaHTMLAttributes,
} from "react";
import { cx } from "class-variance-authority";

import { InputError } from "../InputError/InputError";
import { Skeleton } from "../Skeleton/Skeleton";
import { Typography } from "../Typography/Typography";

import * as styles from "./TextArea.module.scss";

type TextAreaAttribute = DOMAttributes<HTMLTextAreaElement>;

export type TextAreaProps = {
  label?: ReactNode;
  containerClassName?: string;
  textAreaClassName?: string;
  wrapperClassName?: string;
  errorMessage?: string;
  hasError?: boolean;
  icon?: ReactNode;
  prefixIcon?: ReactNode;
  loading?: boolean;
  iconContainerClassName?: string;
  /** Remove the margin from around the element */
  noSpacing?: boolean;
} & TextareaHTMLAttributes<HTMLTextAreaElement>;

const TextArea = ({
  ref,
  id,
  errorMessage,
  label,
  containerClassName,
  textAreaClassName,
  hasError,
  icon,
  prefixIcon,
  onFocus,
  onBlur,
  disabled,
  wrapperClassName,
  loading = false,
  iconContainerClassName,
  noSpacing,
  ...props
}: TextAreaProps & { ref?: Ref<HTMLTextAreaElement> }) => {
  const [isFocused, setIsFocused] = useState(false);

  const handleFocus: TextAreaAttribute["onFocus"] = (event) => {
    setIsFocused(true);
    if (onFocus) onFocus(event);
  };

  const handleBlur: TextAreaAttribute["onBlur"] = (event) => {
    setIsFocused(false);
    if (onBlur) onBlur(event);
  };

  return (
    <div
      className={cx(styles.container, containerClassName, {
        [styles.noSpacing]: noSpacing,
      })}
      data-testid="textarea-container"
    >
      {label && (
        <Typography
          component="label"
          className={styles.label}
          skeleton={{ loading }}
          htmlFor={id}
          noSpacing
        >
          {label}
        </Typography>
      )}
      {loading ? (
        <Skeleton />
      ) : (
        <>
          <div
            data-testid="textarea-wrapper"
            className={cx(
              styles.textAreaWrapper,
              {
                [styles.focus]: isFocused,
                [styles.hasError]: !!errorMessage || hasError,
                [styles.disabled]: !!disabled,
                [styles.hasIcon]: !!icon,
                [styles.hasPrefixIcon]: !!prefixIcon,
              },
              wrapperClassName,
            )}
          >
            {prefixIcon && (
              <div className={styles.prefixIcon}>{prefixIcon}</div>
            )}
            <textarea
              id={id}
              className={cx(styles.textArea, textAreaClassName)}
              ref={ref}
              onFocus={handleFocus}
              onBlur={handleBlur}
              disabled={disabled}
              {...props}
            />
            {icon && (
              <div className={cx(styles.icon, iconContainerClassName)}>
                {icon}
              </div>
            )}
          </div>
          {errorMessage && <InputError message={errorMessage} />}
        </>
      )}
    </div>
  );
};

TextArea.displayName = "TextArea";

export { TextArea };
